import { invariantResponse } from '@epic-web/invariant'
import { useEffect } from 'react'
import { Link, redirect, useSearchParams } from 'react-router'
import { WantToGoButton } from '#app/components/want-to-go-button.tsx'
import { REGION_BY_SLUG, type RegionSlug } from '#app/data/regions.ts'
import {
	canonicalizeLondonBestFilterSlug,
	normalizeLondonAreaSlug,
} from '#app/utils/area-slugs.ts'
// Sort options
const SORT_OPTIONS = [
	{ key: 'rating', label: 'Top Rated' },
	{ key: 'price-low', label: 'Price: Low to High' },
	{ key: 'price-high', label: 'Price: High to Low' },
	{ key: 'distance', label: 'Distance' },
] as const

type SortKey = (typeof SORT_OPTIONS)[number]['key']

// Price range to numeric value for sorting
function priceToNumber(price: string | null): number {
	if (!price) return 3 // Default to middle
	const priceMap: Record<string, number> = {
		'£': 1,
		'££': 2,
		'£££': 3,
		'££££': 4,
	}
	return priceMap[price] ?? 3
}
import { getUserId } from '#app/utils/auth.server.ts'
import {
	getScoreContext,
	getScoreExplainer,
	getScoreSupportText,
} from '#app/utils/consensus.shared.ts'
import {
	cuisineToSlug,
	findCuisineBySlug,
	getCuisineAreaCounts,
	getCuisineCounts,
} from '#app/utils/cuisine-utils.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { getDomainUrl } from '#app/utils/misc.tsx'
import { useGeolocation } from '#app/utils/use-geolocation.ts'
import { type Route } from './+types/london.best.$filter.tsx'

// Local filter options for the UI
const LOCAL_FILTERS = [
	{ key: 'all', label: 'All', param: '' },
	{ key: 'near-me', label: 'Near Me', param: 'near-me' },
	{ key: 'top-rated', label: 'Top Rated', param: 'top-rated' },
	{ key: 'cheap-eats', label: 'Cheap Eats', param: 'cheap-eats' },
	{ key: 'mid-range', label: 'Mid Range', param: 'mid-range' },
	{ key: 'date-night', label: 'Date Night', param: 'date-night' },
	{ key: 'fine-dining', label: 'Fine Dining', param: 'fine-dining' },
] as const

/**
 * Dynamic Collection Route for SEO
 *
 * Handles patterns like:
 * - /london/best/italian → Best Italian in London
 * - /london/best/italian-soho → Best Italian in Soho
 * - /london/best/cheap-eats → Cheap Eats London (priceRange = £)
 * - /london/best/hidden-gems → Hidden Gems London
 * - /london/best/date-night-fitzrovia → Date Night spots in Fitzrovia
 */

type FilterConfig = {
	title: string
	description: string
	whereClause: object
	areaName?: string
	areaSlug?: string
	regionName?: string
	regionSlug?: string
	cuisineName?: string
	isNearMe?: boolean
}

const MIN_INDEXABLE_CUISINE_PAGE_COUNT = 2

// Special filter types
const SPECIAL_FILTERS: Record<
	string,
	{ title: string; whereClause: object; isNearMe?: boolean }
> = {
	'near-me': {
		title: 'Near Me',
		whereClause: {},
		isNearMe: true,
	},
	'top-rated': {
		title: 'Top Rated',
		whereClause: { consensusScore: { gte: 80 } },
	},
	'cheap-eats': {
		title: 'Cheap Eats',
		whereClause: { priceRange: '£' },
	},
	'date-night': {
		title: 'Date Night',
		whereClause: { priceRange: { in: ['££', '£££'] } },
	},
	'fine-dining': {
		title: 'Fine Dining',
		whereClause: { priceRange: '££££' },
	},
	'mid-range': {
		title: 'Mid Range',
		whereClause: { priceRange: '££' },
	},
}

// Build Prisma where clause from filter logic JSON
function buildWhereClause(filterLogic: Record<string, unknown>): object {
	const where: Record<string, unknown> = {}

	if (filterLogic.cuisine) {
		// SQLite LIKE is case-insensitive by default, no mode needed
		where.cuisine = { contains: String(filterLogic.cuisine) }
	}
	if (filterLogic.priceRange) {
		where.priceRange = String(filterLogic.priceRange)
	}
	if (filterLogic.minScore) {
		where.consensusScore = { gte: Number(filterLogic.minScore) }
	}
	if (filterLogic.isHiddenGem) {
		where.isHiddenGem = true
	}
	if (filterLogic.isCultClassic) {
		where.isCultClassic = true
	}
	if (filterLogic.primaryAreaSlug) {
		// Will need to resolve area ID in the loader
		where._primaryAreaSlug = String(filterLogic.primaryAreaSlug)
	}

	return where
}

async function parseFilter(filter: string): Promise<FilterConfig | null> {
	// First, check for a DB-defined collection
	try {
		const dbDefinition = await prisma.collectionDefinition.findFirst({
			where: {
				citySlug: 'london',
				slug: filter.toLowerCase(),
				isActive: true,
			},
		})

		if (dbDefinition) {
			const filterLogic = JSON.parse(dbDefinition.filterLogic) as Record<
				string,
				unknown
			>
			return {
				title: dbDefinition.title,
				description: dbDefinition.intro,
				whereClause: buildWhereClause(filterLogic),
				areaName: filterLogic.primaryAreaSlug
					? String(filterLogic.primaryAreaSlug)
					: undefined,
				cuisineName: filterLogic.cuisine
					? String(filterLogic.cuisine)
					: undefined,
			}
		}
	} catch {
		// Table query failed, fall through to built-in filters
	}

	const parts = filter.toLowerCase().split('-')

	// Check for special filters first
	for (const [key, config] of Object.entries(SPECIAL_FILTERS)) {
		if (filter.startsWith(key)) {
			const suffix = filter.replace(`${key}-`, '')

			if (suffix && suffix !== key) {
				// Check if suffix is a region slug (central, north, east, south, west)
				const region = REGION_BY_SLUG[suffix as RegionSlug]
				if (region) {
					// Special filter + region: e.g., "top-rated-central"
					const isNearMe = config.isNearMe === true
					return {
						title: isNearMe
							? `Restaurants Near You in ${region.name} London`
							: `${config.title} in ${region.name} London`,
						description: isNearMe
							? `Find restaurants closest to you in ${region.name} London. Sorted by distance from your location.`
							: `Discover handpicked ${config.title.toLowerCase()} in ${region.name} London, with what to order first at each place.`,
						whereClause: isNearMe
							? { ...config.whereClause, _regionSlugs: region.areaSlugs }
							: { ...config.whereClause, _regionSlugs: region.areaSlugs },
						regionName: region.name,
						regionSlug: region.slug,
						isNearMe,
					}
				}

				// Check if suffix is an area slug: e.g., "cheap-eats-soho"
				const area = await prisma.tag.findFirst({
					where: { citySlug: 'london', type: 'area', slug: suffix },
				})

				if (area) {
					// For "near-me" filter, don't add score filter and use special title
					const isNearMe = config.isNearMe === true
					return {
						title: isNearMe
							? `Restaurants Near You in ${area.name}`
							: `${config.title} in ${area.name}`,
						description: isNearMe
							? `Find restaurants closest to you in ${area.name}, London. Sorted by distance from your location.`
							: `Discover handpicked ${config.title.toLowerCase()} in ${area.name}, London, with what to order first at each place.`,
						whereClause: isNearMe
							? { ...config.whereClause, primaryAreaId: area.id }
							: { ...config.whereClause, primaryAreaId: area.id },
						areaName: area.name,
						areaSlug: area.slug,
						isNearMe,
					}
				}
			}

			// Special filter only: e.g., "cheap-eats"
			return {
				title: `${config.title} London`,
				description: `Discover handpicked ${config.title.toLowerCase()} in London, with what to order first at each place.`,
				whereClause: {
					...config.whereClause,
				},
			}
		}
	}

	// Check for "restaurants-{area}" or "restaurants-{region}" pattern
	if (filter.startsWith('restaurants-')) {
		const locationSlug = filter.replace('restaurants-', '')
		if (locationSlug) {
			// First check if it's a region (central, north, east, south, west)
			const region = REGION_BY_SLUG[locationSlug as RegionSlug]
			if (region) {
				return {
					title: `Best Restaurants in ${region.name} London`,
					description: `Discover handpicked restaurants in ${region.name} London, with what to order first and quick ordering tips.`,
					whereClause: {
						_regionSlugs: region.areaSlugs,
					},
					regionName: region.name,
					regionSlug: region.slug,
				}
			}

			// Then check if it's an area
			const area = await prisma.tag.findFirst({
				where: { citySlug: 'london', type: 'area', slug: locationSlug },
			})

			if (area) {
				return {
					title: `Best Restaurants in ${area.name}`,
					description: `Discover handpicked restaurants in ${area.name}, London, with what to order first and quick ordering tips.`,
					whereClause: {
						primaryAreaId: area.id,
					},
					areaName: area.name,
					areaSlug: area.slug,
				}
			}
		}
	}

	// Try to parse as cuisine or cuisine-area
	let cuisineName: string | null = null
	let areaSlugParts: string[] = []

	for (let i = parts.length; i >= 1; i--) {
		const potentialCuisineSlug = parts.slice(0, i).join('-')
		const matchingCuisine = await findCuisineBySlug(potentialCuisineSlug)
		if (!matchingCuisine) continue

		cuisineName = matchingCuisine.cuisine
		areaSlugParts = parts.slice(i)
		break
	}

	if (!cuisineName) {
		return null
	}

	const areaSlug = areaSlugParts.join('-')

	if (areaSlug) {
		// First check if it's a region: e.g., "italian-central"
		const region = REGION_BY_SLUG[areaSlug as RegionSlug]
		if (region) {
			return {
				title: `Best ${cuisineName} in ${region.name} London`,
				description: `Discover handpicked ${cuisineName} restaurants in ${region.name} London, with what to order first at each place.`,
				whereClause: {
					cuisine: { contains: cuisineName },
					_regionSlugs: region.areaSlugs,
				},
				regionName: region.name,
				regionSlug: region.slug,
				cuisineName,
			}
		}

		// Cuisine + area: e.g., "italian-soho"
		const area = await prisma.tag.findFirst({
			where: { citySlug: 'london', type: 'area', slug: areaSlug },
		})

		if (area) {
			return {
				title: `Best ${cuisineName} in ${area.name}`,
				description: `Discover handpicked ${cuisineName} restaurants in ${area.name}, London, with what to order first at each place.`,
				whereClause: {
					cuisine: { contains: cuisineName },
					primaryAreaId: area.id,
				},
				areaName: area.name,
				areaSlug: area.slug,
				cuisineName,
			}
		}
	}

	// Cuisine only: e.g., "italian"
	return {
		title: `Best ${cuisineName} in London`,
		description: `Discover handpicked ${cuisineName} restaurants in London, with what to order first at each place.`,
		whereClause: {
			cuisine: { contains: cuisineName },
		},
		cuisineName,
	}
}

export const meta: Route.MetaFunction = ({ data }) => {
	if (!data) {
		return [
			{ title: 'Not found | Good to Order' },
			{ name: 'robots', content: 'noindex' },
		]
	}

	const { filterConfig, canonical } = data
	const year = new Date().getFullYear()
	const restaurantCount = data.restaurants?.length ?? 0

	const normalizedTitle = normalizeFilterMetaTitle(filterConfig.title)
	const title = `${normalizedTitle} (${year}) | What to Order First`
	const description = buildFilterMetaDescription(
		filterConfig.title,
		filterConfig.description,
		restaurantCount,
	)

	return [
		{ title },
		{ name: 'description', content: description.slice(0, 155) },
		...(data.shouldIndex
			? []
			: [{ name: 'robots', content: 'noindex,follow' }]),
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: canonical },
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ rel: 'canonical', href: canonical },
	]
}

function normalizeFilterMetaTitle(title: string): string {
	if (title === 'Cheap Eats London') return 'Cheap Eats in London'
	if (title === 'Date Night London') return 'Date Night Restaurants in London'
	if (title === 'Fine Dining London') return 'Fine Dining in London'
	if (title === 'Mid Range London') return 'Mid-Range Restaurants in London'
	if (title === 'Top Rated London') return 'Top Rated Restaurants in London'
	return title
}

function buildFilterMetaDescription(
	title: string,
	fallbackDescription: string,
	restaurantCount: number,
): string {
	const introCount =
		restaurantCount > 0 ? `${restaurantCount} handpicked spots.` : ''

	if (/^Cheap Eats\b/i.test(title)) {
		return `${introCount} Affordable places worth knowing, with what to order first, rough price, and quick review signal.`.trim()
	}
	if (/^Date Night\b/i.test(title)) {
		return `${introCount} Good date-night options, with what to order first and how strong the review signal is.`.trim()
	}
	if (/^Best Restaurants in\b/i.test(title)) {
		return `${introCount} Handpicked local restaurants, with what to order first at each place.`.trim()
	}
	if (/^Best .* in\b/i.test(title)) {
		return `${introCount} Handpicked picks with what to order first, plus quick review signal for each place.`.trim()
	}
	return `${fallbackDescription} What to order first, plus quick review signal for each place.`
}

export async function loader({ params, request }: Route.LoaderArgs) {
	const { filter } = params
	invariantResponse(filter, 'Filter not found', { status: 404 })

	const url = new URL(request.url)
	const canonicalFilter = canonicalizeLondonBestFilterSlug(filter)
	if (canonicalFilter !== filter.toLowerCase()) {
		url.pathname = `/london/best/${canonicalFilter}`
		throw redirect(url.toString(), 301)
	}

	const filterConfig = await parseFilter(canonicalFilter)
	invariantResponse(filterConfig, 'Invalid filter', { status: 404 })

	// Get user's want-to-go list if logged in
	const userId = await getUserId(request)
	let wantToGoIds: string[] = []
	if (userId) {
		try {
			wantToGoIds = (
				await prisma.wantToGo.findMany({
					where: { userId },
					select: { restaurantId: true },
				})
			).map((w) => w.restaurantId)
		} catch {
			// Table may not exist yet in production
		}
	}

	const localFilter = url.searchParams.get('f') // e.g., ?f=cheap-eats

	const canonical = `${getDomainUrl(request)}/london/best/${canonicalFilter}`
	const domainUrl = getDomainUrl(request)

	// Build the final where clause, resolving any area slugs to IDs
	const whereClause = { ...filterConfig.whereClause } as Record<string, unknown>

	// Apply local filter on top of the base filter (works for cuisine and area pages)
	let activeLocalFilterTitle: string | null = null
	if (localFilter && SPECIAL_FILTERS[localFilter]) {
		const localFilterConfig = SPECIAL_FILTERS[localFilter]
		// Merge local filter where clause
		Object.assign(whereClause, localFilterConfig.whereClause)
		activeLocalFilterTitle = localFilterConfig.title
	}

	// Handle _primaryAreaSlug from DB definitions
	if ('_primaryAreaSlug' in whereClause) {
		const areaSlug = whereClause._primaryAreaSlug as string
		delete whereClause._primaryAreaSlug

		const area = await prisma.tag.findFirst({
			where: {
				citySlug: 'london',
				type: 'area',
				slug: normalizeLondonAreaSlug(areaSlug),
			},
		})

		if (area) {
			whereClause.primaryAreaId = area.id
		}
	}

	// Handle _regionSlugs for region-level filters
	let regionAreaIds: string[] = []
	if ('_regionSlugs' in whereClause) {
		const regionAreaSlugs = whereClause._regionSlugs as string[]
		delete whereClause._regionSlugs

		const areaTags = await prisma.tag.findMany({
			where: {
				citySlug: 'london',
				type: 'area',
				slug: { in: regionAreaSlugs },
			},
			select: { id: true },
		})

		if (areaTags.length > 0) {
			regionAreaIds = areaTags.map((t) => t.id)
			whereClause.primaryAreaId = { in: regionAreaIds }
		}
	}

	const restaurants = await prisma.restaurant.findMany({
		where: {
			status: 'published',
			citySlug: 'london',
			...whereClause,
		},
		orderBy: { consensusScore: 'desc' },
		include: {
			picks: {
				where: { type: 'safe_first' },
				take: 1,
				orderBy: { sortOrder: 'asc' },
			},
			locations: { orderBy: { sortOrder: 'asc' }, take: 1 },
			tags: { include: { tag: true }, where: { tag: { type: 'area' } } },
			primaryArea: true,
		},
	})

	const formattedRestaurants = restaurants.map((r) => {
		const safeFirst = r.picks[0]
		const loc = r.locations[0]
		// Prefer googlePlaceId with proper API format for cross-platform support (desktop + mobile)
		const mapUrl = loc?.googlePlaceId
			? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(r.name + ' London')}&query_place_id=${loc.googlePlaceId}`
			: (loc?.mapUrl ?? r.mapUrl)
		return {
			id: r.id,
			name: r.name,
			slug: r.slug,
			cuisine: r.cuisine,
			priceRange: r.priceRange,
			consensusScore: r.consensusScore,
			confidence: r.confidence,
			visited: r.visited,
			isHiddenGem: r.isHiddenGem,
			isCultClassic: r.isCultClassic,
			safeFirstOrder: safeFirst?.title ?? null,
			safeFirstTip: safeFirst?.tip ?? null,
			mapUrl,
			lat: loc?.latitude ?? null,
			lng: loc?.longitude ?? null,
			primaryAreaId: r.primaryAreaId,
			primaryArea: r.primaryArea
				? {
						id: r.primaryArea.id,
						name: r.primaryArea.name,
						slug: r.primaryArea.slug,
					}
				: null,
			areaTags: r.tags.map((rt) => rt.tag),
			badge: null as string | null,
			isWantToGo: wantToGoIds.includes(r.id),
		}
	})

	// Generate ItemList schema
	const itemList = formattedRestaurants.map((r, index) => ({
		'@type': 'ListItem',
		position: index + 1,
		item: {
			'@type': 'Restaurant',
			name: r.name,
			url: `${domainUrl}/london/restaurants/${r.slug}`,
			servesCuisine: r.cuisine,
			priceRange: r.priceRange,
			address: r.primaryArea
				? {
						'@type': 'PostalAddress',
						addressLocality: r.primaryArea.name,
						addressRegion: 'London',
						addressCountry: 'GB',
					}
				: undefined,
			geo:
				r.lat != null && r.lng != null
					? {
							'@type': 'GeoCoordinates',
							latitude: r.lat,
							longitude: r.lng,
						}
					: undefined,
		},
	}))

	// Determine if this is an area page (shows local filters)
	// Show filters on any page that has an areaSlug (restaurants-{area}, cheap-eats-{area}, etc.)
	const isAreaPage = Boolean(filterConfig.areaSlug)

	// Debug log to verify areaSlug is being set

	// Determine if this is a region page
	const isRegionPage = Boolean(filterConfig.regionSlug)

	// Check if near-me filter is active via query param
	const isNearMeFilter = localFilter === 'near-me'

	const areaCuisineLinks =
		isAreaPage &&
		!filterConfig.cuisineName &&
		typeof whereClause.primaryAreaId === 'string'
			? await getCuisineCounts({
					areaId: whereClause.primaryAreaId,
					minCount: 1,
					limit: 24,
				})
			: []

	const regionCuisineLinks =
		isRegionPage && !filterConfig.cuisineName && regionAreaIds.length > 0
			? await getCuisineCounts({
					areaIds: regionAreaIds,
					minCount: 1,
					limit: 24,
				})
			: []

	const cuisineAreaLinks =
		filterConfig.cuisineName && !filterConfig.areaSlug
			? await getCuisineAreaCounts({
					cuisine: filterConfig.cuisineName,
					areaSlugs: filterConfig.regionSlug
						? REGION_BY_SLUG[filterConfig.regionSlug as RegionSlug]?.areaSlugs
						: undefined,
					minCount: 1,
					limit: 24,
				})
			: []

	const cuisinePageCount = filterConfig.cuisineName
		? ((
				await findCuisineBySlug(cuisineToSlug(filterConfig.cuisineName), {
					areaId:
						filterConfig.areaSlug &&
						typeof whereClause.primaryAreaId === 'string'
							? whereClause.primaryAreaId
							: undefined,
					areaIds: filterConfig.regionSlug ? regionAreaIds : undefined,
				})
			)?.count ?? 0)
		: 0

	const shouldIndex = !filterConfig.cuisineName
		? true
		: cuisinePageCount >= MIN_INDEXABLE_CUISINE_PAGE_COUNT

	return {
		filterConfig: {
			title: filterConfig.title,
			description: filterConfig.description,
			areaName: filterConfig.areaName,
			areaSlug: filterConfig.areaSlug,
			regionName: filterConfig.regionName,
			regionSlug: filterConfig.regionSlug,
			cuisineName: filterConfig.cuisineName,
			cuisineSlug: filterConfig.cuisineName
				? cuisineToSlug(filterConfig.cuisineName)
				: null,
			isNearMe: filterConfig.isNearMe ?? false,
		},
		restaurants: formattedRestaurants,
		itemList,
		canonical,
		domainUrl,
		filter: canonicalFilter,
		isAreaPage,
		isRegionPage,
		areaCuisineLinks,
		regionCuisineLinks,
		cuisineAreaLinks,
		activeLocalFilterTitle,
		isNearMeFilter,
		shouldIndex,
	}
}

// Calculate distance between two points using Haversine formula
function getDistanceKm(
	lat1: number,
	lng1: number,
	lat2: number,
	lng2: number,
): number {
	const R = 6371 // Earth's radius in km
	const dLat = ((lat2 - lat1) * Math.PI) / 180
	const dLng = ((lng2 - lng1) * Math.PI) / 180
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos((lat1 * Math.PI) / 180) *
			Math.cos((lat2 * Math.PI) / 180) *
			Math.sin(dLng / 2) *
			Math.sin(dLng / 2)
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
	return R * c
}

export default function DynamicCollectionPage({
	loaderData,
}: Route.ComponentProps) {
	const {
		filterConfig,
		restaurants,
		itemList,
		domainUrl,
		filter,
		activeLocalFilterTitle,
		isNearMeFilter,
		areaCuisineLinks,
		regionCuisineLinks,
		cuisineAreaLinks,
	} = loaderData

	// URL search params for sort
	const [searchParams, setSearchParams] = useSearchParams()
	const currentSort = (searchParams.get('sort') as SortKey) || 'rating'

	// Geolocation - persisted across page navigations
	const {
		coordinates: userLocation,
		error: geoError,
		loading: isLocating,
		requestLocation,
	} = useGeolocation()

	// Derive error message for display
	const locationError = geoError
		? geoError.code === 1
			? 'Please allow location access to see restaurants near you'
			: 'Unable to get your location'
		: null

	// Is near-me mode active (either via URL or query param)?
	const isNearMeActive = filterConfig.isNearMe || isNearMeFilter

	// Request geolocation when on a "Near Me" page/filter (only if we don't have location yet)
	useEffect(() => {
		if (isNearMeActive && !userLocation && !isLocating) {
			requestLocation()
		}
	}, [isNearMeActive, userLocation, isLocating, requestLocation])

	// Add distance to restaurants if we have user location
	const restaurantsWithDistance = restaurants.map((r) => ({
		...r,
		distance:
			userLocation && r.lat && r.lng
				? getDistanceKm(userLocation.lat, userLocation.lng, r.lat, r.lng)
				: null,
	}))

	// Sort restaurants based on selected sort option
	const sortedRestaurants = [...restaurantsWithDistance].sort((a, b) => {
		// For near-me pages/filters, default to distance sorting when we have location
		const effectiveSort =
			isNearMeActive && userLocation && currentSort === 'rating'
				? 'distance'
				: currentSort

		switch (effectiveSort) {
			case 'distance':
				if (a.distance === null) return 1
				if (b.distance === null) return -1
				return a.distance - b.distance
			case 'price-low':
				return priceToNumber(a.priceRange) - priceToNumber(b.priceRange)
			case 'price-high':
				return priceToNumber(b.priceRange) - priceToNumber(a.priceRange)
			case 'rating':
			default:
				return (b.consensusScore ?? 0) - (a.consensusScore ?? 0)
		}
	})

	// Handle sort change
	const handleSortChange = (newSort: SortKey) => {
		if (newSort === 'rating') {
			// Remove sort param for default
			searchParams.delete('sort')
		} else {
			searchParams.set('sort', newSort)
		}
		setSearchParams(searchParams, { replace: true })
	}

	// Determine which filter is active based on the current URL
	const getActiveFilter = () => {
		const locationSlug = filterConfig.areaSlug || filterConfig.regionSlug
		if (!locationSlug) return 'all'
		if (filter === `restaurants-${locationSlug}`) return 'all'
		for (const f of LOCAL_FILTERS) {
			if (f.param && filter === `${f.param}-${locationSlug}`) return f.key
		}
		return 'all'
	}
	const activeLocalFilter = getActiveFilter()

	const ldItemList = {
		'@context': 'https://schema.org',
		'@type': 'ItemList',
		name: filterConfig.title,
		description: filterConfig.description,
		numberOfItems: restaurants.length,
		itemListElement: itemList,
	}

	const ldBreadcrumb = {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [
			{ '@type': 'ListItem', position: 1, name: 'Home', item: `${domainUrl}/` },
			{
				'@type': 'ListItem',
				position: 2,
				name: 'London',
				item: `${domainUrl}/london`,
			},
			...(filterConfig.areaName
				? [
						{
							'@type': 'ListItem',
							position: 3,
							name: filterConfig.areaName,
							item: `${domainUrl}/london/best/restaurants-${filterConfig.areaSlug}`,
						},
						{ '@type': 'ListItem', position: 4, name: filterConfig.title },
					]
				: [{ '@type': 'ListItem', position: 3, name: filterConfig.title }]),
		],
	}

	return (
		<main className="min-h-screen bg-[#05050a] text-[#f8f8ff]">
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(ldItemList) }}
			/>
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(ldBreadcrumb) }}
			/>

			<section className="border-b border-[#1a1a2e] bg-gradient-to-b from-[#000000] via-[#05050a] to-[#0a001f]">
				<div className="container mx-auto px-6 py-12 lg:py-16">
					<nav className="mb-6 flex flex-wrap items-center gap-2 text-sm text-[#b0b0e0]">
						<Link to="/" className="hover:text-[#422ad5]">
							Home
						</Link>
						<span>/</span>
						<Link to="/london" className="hover:text-[#422ad5]">
							London
						</Link>
						{filterConfig.areaName && (
							<>
								<span>/</span>
								<Link
									to={`/london/best/restaurants-${filterConfig.areaSlug}`}
									className="hover:text-[#422ad5]"
								>
									{filterConfig.areaName}
								</Link>
							</>
						)}
						<span>/</span>
						<span className="text-[#f8f8ff]">{filterConfig.title}</span>
					</nav>

					<div className="max-w-3xl space-y-4">
						<p className="font-mono text-[11px] tracking-[0.3em] text-gray-500 uppercase">
							{filterConfig.cuisineName
								? 'Cuisine Guide'
								: 'Curated Collection'}
						</p>
						<h1 className="text-4xl font-black tracking-tight text-[#f8f8ff] sm:text-5xl">
							{activeLocalFilterTitle
								? `${activeLocalFilterTitle} — ${filterConfig.title}`
								: filterConfig.title}
						</h1>
						<p className="text-lg text-[#b0b0e0]">{filterConfig.description}</p>
						<p className="max-w-2xl text-sm text-[#7070a0]">
							Every place here is handpicked. Signal score shows how strong and
							consistent the review coverage is, not a star rating.
						</p>
						{isNearMeActive && isLocating && (
							<p className="text-sm text-[#b0b0e0]">Getting your location...</p>
						)}
						{isNearMeActive && locationError && (
							<p className="text-sm text-[#ff6b6b]">{locationError}</p>
						)}
					</div>

					{/* Local Filters - Show on all filter pages */}
					<div className="mt-8">
						<p className="mb-3 text-xs tracking-[0.15em] text-[#7070a0] uppercase">
							Filter by
						</p>
						<div className="flex flex-wrap gap-2">
							{LOCAL_FILTERS.map((f) => {
								const locationSlug =
									filterConfig.areaSlug || filterConfig.regionSlug
								const currentLocalFilter = searchParams.get('f')

								// For cuisine pages (e.g., korean-central), use query params to preserve cuisine context
								if (filterConfig.cuisineName) {
									const isActive = f.param
										? currentLocalFilter === f.param
										: !currentLocalFilter // "All" is active when no ?f param
									const href = f.param
										? `/london/best/${filter}?f=${f.param}`
										: `/london/best/${filter}` // "All" removes the filter param
									return (
										<Link
											key={f.key}
											to={href}
											prefetch="intent"
											className={`btn btn-sm ${
												isActive
													? 'border-[#422ad5] bg-[#422ad5] text-white'
													: 'border-[#1a1a2e] bg-[#0d0d15] text-[#f8f8ff] hover:border-[#422ad5]'
											}`}
										>
											{f.label}
										</Link>
									)
								}

								// For non-cuisine pages (area/region pages), use URL-based filters
								const isActive = locationSlug
									? activeLocalFilter === f.key
									: f.param
										? filter === f.param
										: false // "All" not applicable for city-wide
								// Generate href based on whether we have a location
								const href = locationSlug
									? f.param
										? `/london/best/${f.param}-${locationSlug}`
										: `/london/best/restaurants-${locationSlug}`
									: f.param
										? `/london/best/${f.param}`
										: '/london' // "All" goes to London index for city-wide
								return (
									<Link
										key={f.key}
										to={href}
										prefetch="intent"
										className={`btn btn-sm ${
											isActive
												? 'border-[#422ad5] bg-[#422ad5] text-white'
												: 'border-[#1a1a2e] bg-[#0d0d15] text-[#f8f8ff] hover:border-[#422ad5]'
										}`}
									>
										{f.label}
									</Link>
								)
							})}
						</div>
					</div>

					{areaCuisineLinks.length > 0 && (
						<div className="mt-8">
							<p className="mb-3 text-xs tracking-[0.15em] text-[#7070a0] uppercase">
								Cuisines in {filterConfig.areaName}
							</p>
							<div className="flex flex-wrap gap-2">
								{areaCuisineLinks.map((cuisine) => (
									<Link
										key={cuisine.slug}
										to={`/london/best/${cuisine.slug}-${filterConfig.areaSlug}`}
										prefetch="intent"
										className="badge border border-[#422ad5] bg-[#0d0d15] px-4 py-3 text-sm text-[#f8f8ff] transition hover:bg-[#422ad5]/20"
									>
										{cuisine.cuisine}
										<span className="ml-2 text-[#7070a0]">{cuisine.count}</span>
									</Link>
								))}
							</div>
						</div>
					)}

					{regionCuisineLinks.length > 0 && (
						<div className="mt-8">
							<p className="mb-3 text-xs tracking-[0.15em] text-[#7070a0] uppercase">
								Cuisines in {filterConfig.regionName} London
							</p>
							<div className="flex flex-wrap gap-2">
								{regionCuisineLinks.map((cuisine) => (
									<Link
										key={cuisine.slug}
										to={`/london/best/${cuisine.slug}-${filterConfig.regionSlug}`}
										prefetch="intent"
										className="badge border border-[#422ad5] bg-[#0d0d15] px-4 py-3 text-sm text-[#f8f8ff] transition hover:bg-[#422ad5]/20"
									>
										{cuisine.cuisine}
										<span className="ml-2 text-[#7070a0]">{cuisine.count}</span>
									</Link>
								))}
							</div>
						</div>
					)}

					{cuisineAreaLinks.length > 0 && (
						<div className="mt-8">
							<p className="mb-3 text-xs tracking-[0.15em] text-[#7070a0] uppercase">
								{filterConfig.cuisineName} by neighbourhood
							</p>
							<div className="flex flex-wrap gap-2">
								{cuisineAreaLinks.map((area) => (
									<Link
										key={area.areaSlug}
										to={`/london/best/${filterConfig.cuisineSlug}-${area.areaSlug}`}
										prefetch="intent"
										className="badge border border-[#00e0ff] bg-[#0d0d15] px-4 py-3 text-sm text-[#f8f8ff] transition hover:bg-[#00e0ff]/20"
									>
										{area.areaName}
										<span className="ml-2 text-[#7070a0]">{area.count}</span>
									</Link>
								))}
							</div>
						</div>
					)}
				</div>
			</section>

			<section className="container mx-auto px-6 py-10">
				{/* Sort Controls */}
				<div className="mb-6 flex flex-wrap items-center justify-between gap-4">
					<p className="text-sm text-[#7070a0]">
						{sortedRestaurants.length} restaurant
						{sortedRestaurants.length !== 1 ? 's' : ''}
					</p>
					<div className="flex items-center gap-2">
						<label htmlFor="sort-select" className="text-sm text-[#7070a0]">
							Sort by:
						</label>
						<select
							id="sort-select"
							value={currentSort}
							onChange={(e) => handleSortChange(e.target.value as SortKey)}
							className="cursor-pointer rounded-lg border border-[#1a1a2e] bg-[#0d0d15] px-3 py-2 text-sm text-[#f8f8ff] focus:border-[#422ad5] focus:outline-none"
						>
							{SORT_OPTIONS.filter(
								(opt) => opt.key !== 'distance' || userLocation,
							).map((opt) => (
								<option key={opt.key} value={opt.key}>
									{opt.label}
								</option>
							))}
						</select>
					</div>
				</div>

				<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
					{sortedRestaurants.map((restaurant, index) => (
						<RestaurantCard
							key={restaurant.id}
							restaurant={restaurant}
							rank={index + 1}
						/>
					))}
				</div>

				{sortedRestaurants.length === 0 && (
					<div className="py-16 text-center">
						<p className="text-[#b0b0e0]">
							No restaurants found matching this filter.
						</p>
						<Link to="/london" className="btn btn-primary mt-4">
							Browse all London restaurants
						</Link>
					</div>
				)}
			</section>

			{/* Related collections for internal linking */}
			<section className="container mx-auto px-6 pb-14">
				<h2 className="mb-6 text-2xl font-black tracking-tight text-[#f8f8ff]">
					Explore more collections
				</h2>
				<div className="flex flex-wrap gap-3">
					<Link
						to="/london/best/top-rated"
						className="badge border border-[#00e0ff] bg-[#0d0d15] px-4 py-2 text-[#00e0ff] transition hover:bg-[#00e0ff]/20"
					>
						Top Rated
					</Link>
					<Link
						to="/london/best/cheap-eats"
						className="badge border border-[#22c55e] bg-[#0d0d15] px-4 py-2 text-[#22c55e] transition hover:bg-[#22c55e]/20"
					>
						Cheap Eats
					</Link>
					<Link
						to="/london/best/date-night"
						className="badge border border-[#ff9f1c] bg-[#0d0d15] px-4 py-2 text-[#ff9f1c] transition hover:bg-[#ff9f1c]/20"
					>
						Date Night
					</Link>
					{filterConfig.cuisineName !== 'Italian' && (
						<Link
							to="/london/best/italian"
							className="badge border border-[#422ad5] bg-[#0d0d15] px-4 py-2 text-[#f8f8ff] transition hover:bg-[#422ad5]/20"
						>
							Best Italian
						</Link>
					)}
					{filterConfig.cuisineName !== 'Japanese' && (
						<Link
							to="/london/best/japanese"
							className="badge border border-[#422ad5] bg-[#0d0d15] px-4 py-2 text-[#f8f8ff] transition hover:bg-[#422ad5]/20"
						>
							Best Japanese
						</Link>
					)}
					{filterConfig.cuisineName !== 'Korean' && (
						<Link
							to="/london/best/korean"
							className="badge border border-[#422ad5] bg-[#0d0d15] px-4 py-2 text-[#f8f8ff] transition hover:bg-[#422ad5]/20"
						>
							Best Korean
						</Link>
					)}
				</div>
			</section>
		</main>
	)
}

function RestaurantCard({
	restaurant,
	rank,
}: {
	restaurant: {
		id: string
		name: string
		slug: string
		cuisine: string
		priceRange: string
		visited: boolean
		confidence: string
		mapUrl: string | null
		safeFirstOrder: string | null
		safeFirstTip: string | null
		areaTags: { id: string; slug: string; name: string }[]
		primaryArea: { id: string; name: string; slug: string } | null
		badge: string | null
		consensusScore: number
		isHiddenGem?: boolean
		isCultClassic?: boolean
		distance?: number | null
		isWantToGo?: boolean
	}
	rank: number
}) {
	const scoreContext = getScoreContext(restaurant.consensusScore ?? 0)

	return (
		<article className="card border border-[#1a1a2e] bg-[#0d0d15] shadow-[0_0_20px_rgba(66,42,213,0.12)] transition hover:shadow-[0_0_28px_rgba(0,224,255,0.2)]">
			<div className="card-body space-y-4 p-5">
				{/* Header with name and score */}
				<div className="flex items-start justify-between gap-3">
					<div className="flex items-center gap-3">
						<span className="badge badge-lg border border-[#422ad5] font-bold text-[#422ad5]">
							{rank}
						</span>
						<div>
							<Link
								to={`/london/restaurants/${restaurant.slug}`}
								className="card-title text-[#f8f8ff] hover:text-[#00e0ff]"
							>
								{restaurant.name}
							</Link>
							<p className="mt-1 text-xs tracking-[0.1em] text-[#b0b0e0] uppercase">
								{restaurant.cuisine} · {restaurant.priceRange}
								{restaurant.primaryArea && ` · ${restaurant.primaryArea.name}`}
								{restaurant.distance != null && (
									<span className="text-[#00e0ff]">
										{' '}
										·{' '}
										{restaurant.distance < 1
											? `${Math.round(restaurant.distance * 1000)}m`
											: `${restaurant.distance.toFixed(1)}km`}
									</span>
								)}
							</p>
						</div>
					</div>
					<span
						className="badge badge-sm gap-1 font-semibold"
						style={{
							borderColor: scoreContext.color,
							color: scoreContext.color,
							backgroundColor: `${scoreContext.color}1a`,
						}}
						title={getScoreExplainer(restaurant.consensusScore ?? 0)}
					>
						{restaurant.consensusScore ?? 0}
						<span className="text-[10px] tracking-[0.18em] text-[#b0b0e0] uppercase">
							Signal
						</span>
					</span>
				</div>

				{/* Badges row */}
				<div className="flex flex-wrap gap-2">
					{restaurant.visited && (
						<span className="badge badge-sm border border-[#00e0ff] tracking-[0.1em] text-[#00e0ff] uppercase">
							✓ Visited
						</span>
					)}
					{restaurant.isHiddenGem && (
						<span className="badge badge-sm border border-[#00e0ff] tracking-[0.1em] text-[#00e0ff] uppercase">
							Hidden Gem
						</span>
					)}
					{restaurant.isCultClassic && (
						<span className="badge badge-sm border border-[#ff9f1c] tracking-[0.1em] text-[#ff9f1c] uppercase">
							Cult Classic
						</span>
					)}
					{(restaurant.consensusScore ?? 0) < 45 && !restaurant.isHiddenGem && (
						<span className="badge badge-sm border border-[#7070a0] tracking-[0.1em] text-[#b0b0e0] uppercase">
							Light Coverage
						</span>
					)}
				</div>

				{/* Safe first order */}
				{restaurant.safeFirstOrder && (
					<div className="rounded-lg border border-[#1a1a2e] bg-[#151525] p-3">
						<p className="mb-1 text-xs tracking-wide text-[#b0b0e0] uppercase">
							Start with
						</p>
						<p className="font-semibold text-[#f8f8ff]">
							{restaurant.safeFirstOrder}
						</p>
						{restaurant.safeFirstTip && (
							<p className="mt-1 text-sm text-[#b0b0e0]">
								{restaurant.safeFirstTip}
							</p>
						)}
					</div>
				)}

				<p className="text-xs text-[#7070a0]">
					{getScoreSupportText(restaurant.consensusScore ?? 0)}
				</p>

				{/* Actions */}
				<div className="card-actions items-center justify-between pt-2">
					{restaurant.mapUrl && (
						<a
							href={restaurant.mapUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="btn btn-ghost btn-sm gap-1 text-[#f8f8ff] hover:text-[#00e0ff]"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth={1.5}
								stroke="currentColor"
								className="size-4"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
								/>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
								/>
							</svg>
							Map
						</a>
					)}
					<div className="flex items-center gap-2">
						<WantToGoButton
							restaurantId={restaurant.id}
							initialIsWantToGo={restaurant.isWantToGo}
							size="sm"
						/>
						<Link
							to={`/london/restaurants/${restaurant.slug}`}
							className="btn btn-primary btn-sm"
						>
							What to order →
						</Link>
					</div>
				</div>
			</div>
		</article>
	)
}
